/**
 * Shared Free / Professional / Enterprise tier cards (Phase 11).
 * Used on the public landing page and /pricing so tiers are always visible.
 */
import {
	Box,
	Button,
	Chip,
	Stack,
	Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { Link as RouterLink } from "react-router-dom";

import { useAuth } from "../../auth";
import { GlassSurface } from "../../layouts/components/GlassSurface";

export interface PlanCardSpec {
	id: "free" | "professional" | "enterprise";
	name: string;
	price: string;
	tagline: string;
	highlights: string[];
	ctaLabel: string;
	ctaTo: string;
	variant?: "contained" | "outlined";
	badge?: string;
}

export const PLAN_TIER_SPECS: PlanCardSpec[] = [
	{
		id: "free",
		name: "Free",
		price: "$0",
		tagline: "Explore FlahaSOIL with Preliminary analysis",
		highlights: [
			"Email verification required",
			"5 lifetime Preliminary tests",
			"Results & interpretation online",
			"No projects or PDF reports",
			"Personal workspace only",
		],
		ctaLabel: "Create free account",
		ctaTo: "/register",
		variant: "outlined",
	},
	{
		id: "professional",
		name: "Professional",
		price: "$40/month",
		tagline: "Routine soil testing for labs and consultants",
		highlights: [
			"Organization workspace (up to 3 seats)",
			"Projects & sample management",
			"Preliminary + Moderate levels",
			"Standard FlahaSOIL PDF reports",
			"~40 tests per billing month",
		],
		ctaLabel: "Upgrade to Professional",
		ctaTo: "/account?tab=billing",
		variant: "contained",
		badge: "Most popular",
	},
	{
		id: "enterprise",
		name: "Enterprise",
		price: "Contract",
		tagline: "Advanced programmes with contract limits",
		highlights: [
			"Advanced laboratory test level",
			"Contract-defined seats & volume",
			"Enterprise branding options",
			"Optional API / integrations",
			"Priority support under contract",
		],
		ctaLabel: "Contact us",
		ctaTo: "/enterprise/contact",
		variant: "outlined",
	},
];

function resolveCtaTo(
	plan: PlanCardSpec,
	authStatus: string
): string {
	if (plan.id === "free") {
		return authStatus === "authenticated" ? "/dashboard" : "/register";
	}
	if (plan.id === "professional") {
		if (authStatus === "authenticated") return "/account?tab=billing";
		return "/login?next=/account%3Ftab%3Dbilling";
	}
	return plan.ctaTo;
}

function resolveCtaLabel(plan: PlanCardSpec, authStatus: string): string {
	if (plan.id === "free" && authStatus === "authenticated") {
		return "Open workspace";
	}
	if (plan.id === "professional" && authStatus !== "authenticated") {
		return "Sign in to upgrade";
	}
	return plan.ctaLabel;
}

export interface PlanTierCardsProps {
	/** Optional id prefix for heading anchors on the landing page. */
	headingId?: string;
	/** Compact density for embedding. */
	dense?: boolean;
}

export function PlanTierCards({
	headingId = "pricing-plans-title",
	dense = false,
}: PlanTierCardsProps) {
	const { status } = useAuth();

	return (
		<Box
			component="section"
			aria-labelledby={headingId}
			data-testid="plan-tier-cards"
		>
			<Stack spacing={1} sx={{ mb: dense ? 2.5 : 3.5, maxWidth: 760 }}>
				<Typography
					variant="overline"
					sx={{ color: "workspace.green", letterSpacing: "0.12em" }}
				>
					Plans & pricing
				</Typography>
				<Typography id={headingId} component="h2" variant="h3">
					Free · Professional · Enterprise
				</Typography>
				<Typography sx={{ color: "workspace.textSecondary" }}>
					Commercial access is separate from scientific completeness. Choose a
					tier that matches how you work with soil evidence.
				</Typography>
			</Stack>

			<Box
				sx={{
					display: "grid",
					gap: 2.5,
					gridTemplateColumns: {
						xs: "1fr",
						md: "repeat(3, minmax(0, 1fr))",
					},
					alignItems: "stretch",
				}}
			>
				{PLAN_TIER_SPECS.map((plan) => {
					const popular = plan.id === "professional";
					const ctaTo = resolveCtaTo(plan, status);
					const ctaLabel = resolveCtaLabel(plan, status);
					return (
						<GlassSurface
							key={plan.id}
							component="article"
							intensity={popular ? "strong" : "standard"}
							depth={popular ? "raised" : "flat"}
							data-testid={`plan-card-${plan.id}`}
							sx={
								popular
									? {
											p: dense ? 2.25 : 2.75,
											display: "flex",
											flexDirection: "column",
											height: "100%",
											minWidth: 0,
											borderColor: "rgba(119,169,87,0.55)",
											boxShadow: "0 12px 40px rgba(119,169,87,0.12)",
										}
									: {
											p: dense ? 2.25 : 2.75,
											display: "flex",
											flexDirection: "column",
											height: "100%",
											minWidth: 0,
										}
							}
						>
							<Stack
								direction="row"
								spacing={1}
								alignItems="center"
								sx={{ mb: 1 }}
								flexWrap="wrap"
								useFlexGap
							>
								<Typography variant="h6" fontWeight={800}>
									{plan.name}
								</Typography>
								{plan.badge && (
									<Chip size="small" color="primary" label={plan.badge} />
								)}
							</Stack>
							<Typography
								variant="h3"
								component="p"
								sx={{ mb: 0.5, fontWeight: 800, letterSpacing: "-0.02em" }}
							>
								{plan.price}
							</Typography>
							<Typography
								variant="body2"
								sx={{
									color: "workspace.textSecondary",
									mb: 2,
									minHeight: { md: 44 },
								}}
							>
								{plan.tagline}
							</Typography>
							<Stack spacing={1.1} sx={{ flex: 1, mb: 2.5 }}>
								{plan.highlights.map((h) => (
									<Stack
										key={h}
										direction="row"
										spacing={1}
										alignItems="flex-start"
									>
										<CheckIcon
											sx={{
												fontSize: 18,
												color: "workspace.green",
												mt: 0.15,
												flexShrink: 0,
											}}
											aria-hidden
										/>
										<Typography variant="body2">{h}</Typography>
									</Stack>
								))}
							</Stack>
							<Button
								component={RouterLink}
								to={ctaTo}
								variant={plan.variant ?? "outlined"}
								fullWidth
								size="large"
							>
								{ctaLabel}
							</Button>
						</GlassSurface>
					);
				})}
			</Box>
		</Box>
	);
}
